import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, MessageCircle, MessageSquare, X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { CallRequestDialog } from "./CallRequestDialog";
import ChatWidget from "./ChatWidget";

export default function MessengerBubble() {
  const { t, ts, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);  // panneau de clavardage en direct
  const estPersonnel = !!user && (user.role === "admin" || user.role === "vendeur");

  // Ouvrir le tchat DIRECTEMENT depuis un lien (demande du propriétaire, 2026-11-01) :
  // les courriels invitent les gens SANS Facebook à écrire via la fenêtre de tchat
  // du site — l'adresse mrtrophee.ca/?tchat=1 ouvre le panneau aussitôt.
  // PERSONNEL connecté : le lien mène au TABLEAU DE BORD des conversations
  // (Clients → Clavardage en direct), pas au tchat visiteur.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get("tchat") === "1") {
        if (estPersonnel) {
          navigate("/admin?onglet=assistant");
        } else {
          setChatOpen(true);
        }
        p.delete("tchat");
        const qs = p.toString();
        window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
      }
    } catch { /* paramètre ignoré */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estPersonnel]);

  // Aviser les autres flottants (bouton « Haut de page ») que le menu est ouvert
  // pour qu'ils s'effacent — sinon la flèche chevauchait « Demander un appel »
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("mt-bubble", { detail: open }));
  }, [open]);

  return (
    <div className="fixed bottom-24 right-6 z-50 flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <>
            {/* Clavardage en direct — visiteur : panneau de tchat ; PERSONNEL
                connecté : page des conversations du tableau de bord (2026-11-01) */}
            <motion.button
              key="assistant"
              onClick={() => {
                setOpen(false);
                if (estPersonnel) {
                  navigate("/admin?onglet=assistant");
                } else {
                  setChatOpen(true);
                }
              }}
              data-testid="bubble-assistant"
              initial={{ opacity: 0, y: 12, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.9 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-center gap-2.5 bg-surface border border-gold/40 text-gold rounded-full px-5 py-3 text-sm font-semibold shadow-lg hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              <Bot className="w-5 h-5" /> {lang === "en" ? "Live chat" : "Clavardage en direct"}
            </motion.button>
            <motion.a
              key="messenger"
              href="https://m.me/trophee.ca" target="_blank" rel="noreferrer"
              data-testid="bubble-messenger"
              initial={{ opacity: 0, y: 12, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.9 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-center gap-2.5 bg-surface border border-gold/40 text-gold rounded-full px-5 py-3 text-sm font-semibold shadow-lg hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              <MessageCircle className="w-5 h-5" /> Messenger
            </motion.a>
            <motion.a
              key="texto"
              href="sms:+15145656665"
              data-testid="bubble-texto"
              initial={{ opacity: 0, y: 12, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.9 }}
              transition={{ duration: 0.25, delay: 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-center gap-2.5 bg-surface border border-gold/40 text-gold rounded-full px-5 py-3 text-sm font-semibold shadow-lg hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              <MessageSquare className="w-5 h-5" /> {t("footer.texto")}
            </motion.a>
            <motion.div
              key="call"
              initial={{ opacity: 0, y: 12, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.9 }}
              transition={{ duration: 0.25, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            >
              <CallRequestDialog testid="bubble-call"
                className="flex items-center gap-2.5 bg-gold text-ink rounded-full px-5 py-3 text-sm font-bold shadow-lg hover:bg-champagne transition-colors duration-300" />
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <motion.button
        onClick={() => setOpen((v) => !v)}
        data-testid="messenger-bubble"
        aria-label={ts("contact.messenger")}
        initial={{ opacity: 0, scale: 0.6, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0, rotate: open ? 90 : 0 }}
        transition={{ delay: open ? 0 : 1.6, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ scale: 1.08 }}
        className="relative bg-gold text-ink rounded-full p-4 shadow-[0_8px_30px_rgba(212,175,55,0.35)] hover:bg-champagne transition-colors duration-300"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        {!open && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-ink" />}
      </motion.button>
      <ChatWidget open={chatOpen} onClose={() => setChatOpen(false)} />
    </div>
  );
}
